import { Component } from '@theme/component';

/**
 * A custom element that displays product siblings sharing the same style code.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} list - The list element for the products.
 * @extends {Component<Refs>}
 */
class NjProductSiblingsComponent extends Component {
  requiredRefs = ['list'];

  connectedCallback() {
    super.connectedCallback();
    this.#loadProductSiblings();
  }

  /**
   * Fetches products with matching style code via Storefront API.
   */
  async #loadProductSiblings() {
    const styleCode = this.dataset.styleCode;
    const collection = this.dataset.collection;
    const currentProductId = this.dataset.currentProductId;
    const currentProductHandle = this.dataset.currentProductHandle;
    const currentProductTitle = this.dataset.currentProductTitle;
    const currentProductColorPattern = this.dataset.currentProductColorPattern;
    const currentProductImage = this.dataset.currentProductImage;
    const currentProductColorHex = this.dataset.currentProductColorHex;
    const currentProductSwatchImage = this.dataset.currentProductSwatchImage;
    const displayType = this.dataset.displayType || 'image';

    if (!styleCode) {
      console.warn('No style code provided for product siblings');
      return;
    }

    const apiToken = '86c496dfca82c5f32efd594deacec52e';

    try {
      const siblings = await this.#fetchSiblingProducts(styleCode, collection, apiToken);
      
      if (siblings.length === 0) {
        // Only show current product if no siblings found
        return;
      } else {
        // Show siblings component
        this.style.display = 'block';
      }

      this.#renderProductList(siblings, {
        id: currentProductId,
        handle: currentProductHandle,
        title: currentProductTitle,
        colorPattern: currentProductColorPattern || currentProductTitle,
        image: currentProductImage,
        colorHex: currentProductColorHex,
        swatchImageUrl: currentProductSwatchImage,
        displayType: displayType
      });
    } catch (error) {
      console.error('Error loading product siblings:', error);
    }
  }

  /**
   * Fetches sibling products from Storefront API.
   * @param {string} styleCode
   * @param {string} apiToken
   * @returns {Promise<Array>}
   */
  async #fetchSiblingProducts(styleCode, collection, apiToken) {
    // Fallback to 'all' base collection if we couldn't extract the brand collection
    if (!collection || collection == '') collection = 'all'; 
    const query = `
      query GetProductsByStyleCode {
        collection(handle: "${collection}") {
          products(
            first: 250
            filters: {
              productMetafield: {
                namespace: "custom"
                key: "style_code"
                value: "${styleCode}"
              }
            }
          ) {
            edges {
              node {
                id
                handle
                title
                availableForSale
                featuredImage {
                  url(transform: { maxWidth: 100, maxHeight: 100 })
                }
                colorPattern: metafield(namespace: "shopify", key: "color-pattern") {
                  references(first: 10) {
                    nodes {
                      ... on Metaobject {
                        fields {
                          key
                          value
                          reference {
                            ... on MediaImage {
                              image {
                                url(transform: { maxWidth: 64, maxHeight: 64 })
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const response = await fetch(`https://${window.Shopify.shop}/api/2024-01/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': apiToken,
      },
      body: JSON.stringify({ query })
    });

    if (!response.ok) {
      throw new Error(`Storefront API request failed: ${response.statusText}`);
    }

    const data = await response.json();
    
    if (data.errors) {
      throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
    }

    // Return filtered products from the collection
    return data.data.collection.products.edges
      // Exclude out of stock items
      .filter(edge => edge.node.availableForSale)
      // Exclude items with color pattern "O/C"
      .filter(edge => {
        if (edge.node.colorPattern?.references?.nodes?.[0]?.fields) {
          const fields = edge.node.colorPattern.references.nodes[0].fields;
          const labelField = fields.find(f => f.key === 'label');
          if (labelField && labelField.value === 'O/C' || labelField.value === 'O/C') {
            return false;
          }
        }
        return true;
      })
      .map(edge => {
      // Extract color data from metaobject references
      let colorLabel = edge.node.title;
      let colorHex = null;
      let swatchImageUrl = null;
      
      if (edge.node.colorPattern?.references?.nodes?.[0]?.fields) {
        const fields = edge.node.colorPattern.references.nodes[0].fields;
        
        const labelField = fields.find(f => f.key === 'label');
        if (labelField) colorLabel = labelField.value;
        
        const colorField = fields.find(f => f.key === 'color');
        if (colorField) colorHex = colorField.value;
        
        const swatchField = fields.find(f => f.key === 'swatch_image');
        if (swatchField?.reference?.image) {
          swatchImageUrl = swatchField.reference.image.url;
        }
      }
      
      return {
        id: edge.node.id.replace('gid://shopify/Product/', ''),
        handle: edge.node.handle,
        title: edge.node.title,
        colorPattern: colorLabel,
        colorHex: colorHex,
        swatchImageUrl: swatchImageUrl,
        imageUrl: edge.node.featuredImage?.url || null
      };
    });
  }

  /**
   * Renders the product list, ensuring current product is first.
   * @param {Array} siblings
   * @param {object} currentProduct
   */
  #renderProductList(siblings, currentProduct) {
    const listHTML = [];

    // Add current product first
    listHTML.push(this.#createProductListItem(
      currentProduct.handle,
      currentProduct.colorPattern || currentProduct.title,
      currentProduct.image,
      true,
      currentProduct,
      currentProduct.displayType
    ));

    // Add other siblings
    siblings.forEach(product => {
      if (product.id !== currentProduct.id) {
        listHTML.push(this.#createProductListItem(
          product.handle,
          product.colorPattern,
          product.imageUrl,
          false,
          product,
          currentProduct.displayType
        ));
      }
    });

    this.refs.list.innerHTML = listHTML.join('');

    // Setup hover listeners for swatch type
    this.#setupSwatchHoverListeners(
      currentProduct.displayType,
      currentProduct.colorPattern || currentProduct.title
    );
  }

  /**
   * Creates HTML for a single product list item.
   * @param {string} handle
   * @param {string} title
   * @param {string|null} imageUrl
   * @param {boolean} isCurrent
   * @param {object} product
   * @param {string} displayType
   * @returns {string}
   */
  #createProductListItem(handle, title, imageUrl, isCurrent, product, displayType) {
    const currentClass = isCurrent ? ' data-current="true"' : '';
    let contentHTML = '';
    let typeClass = '';
    
    if (displayType === 'name') {
      // Name only - no image or swatch
      typeClass = ' type-name';
      contentHTML = `<span>${this.#escapeHtml(title)}</span>`;
    } else if (displayType === 'swatch') {
      // Swatch - use color hex or swatch image
      typeClass = ' type-swatch';
      if (product.swatchImageUrl) {
        typeClass += ' swatch-image';
        contentHTML = `
          <span class="swatch-wrapper">
            <img src="${product.swatchImageUrl}" alt="${this.#escapeHtml(title)}" width="64" height="64" loading="lazy">
          </span>
          <span class="swatch-label">${this.#escapeHtml(title)}</span>
        `;
      } else if (product.colorHex) {
        typeClass += ' swatch-color';
        contentHTML = `
          <span class="swatch-wrapper">
            <span class="swatch-color-box" style="background-color: ${this.#escapeHtml(product.colorHex)}"></span>
          </span>
          <span class="swatch-label">${this.#escapeHtml(title)}</span>
        `;
      } else {
        contentHTML = `<span>${this.#escapeHtml(title)}</span>`;
      }
    } else {
      // Image (default)
      typeClass = ' type-image';
      const imageHTML = imageUrl 
        ? `<img src="${imageUrl}" alt="${this.#escapeHtml(title)}" width="64" height="64" loading="lazy">`
        : '';
      contentHTML = `
        ${imageHTML}
        <span>${this.#escapeHtml(title)}</span>
      `;
    }

    return `
      <li${currentClass} class="${typeClass.trim()}" data-product-color-pattern="${this.#escapeHtml(title)}">
        <a href="/products/${handle}">
          ${contentHTML}
        </a>
      </li>
    `;
  }

  /**
   * Escapes HTML to prevent XSS.
   * @param {string} str
   * @returns {string}
   */
  #escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Sets up hover listeners for swatch type to update the legend.
   * @param {string} displayType
   * @param {string} currentColorPattern
   */
  #setupSwatchHoverListeners(displayType, currentColorPattern) {
    if (displayType !== 'swatch') return;
    
    const legend = this.querySelector('legend');
    if (!legend) return;
    
    const legendText = legend.textContent.trim();
    const items = this.refs.list.querySelectorAll('li');
    
    // Set initial legend text with current color
    legend.textContent = `${legendText}: ${currentColorPattern}`;
    
    items.forEach(item => {
      const colorPattern = item.dataset.productColorPattern;
      
      item.addEventListener('mouseenter', () => {
        legend.textContent = `${legendText}: ${colorPattern}`;
      });
      
      item.addEventListener('mouseleave', () => {
        // Revert to current color on mouse leave
        const currentItem = this.refs.list.querySelector('li[data-current="true"]');
        const currentColor = currentItem?.dataset.productColorPattern || currentColorPattern;
        legend.textContent = `${legendText}: ${currentColor}`;
      });
    });
  }
}

if (!customElements.get('nj-product-siblings')) {
  customElements.define('nj-product-siblings', NjProductSiblingsComponent);
}
